import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [carerUnlocked, setCarerUnlocked] = useState(sessionStorage.getItem("rb_carer_unlocked") === "1");
  const [evening, setEvening] = useState(localStorage.getItem("rb_evening") === "1");
  const [pro, setPro] = useState(null); // { paid, state: unlimited|ready|active|locked, seconds_left, unlocks_at }

  const refreshPro = useCallback(async () => {
    try { const { data } = await api.get("/account/pro-status"); setPro(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user) { setPro(null); return; }
    refreshPro();
    const id = setInterval(refreshPro, 60000);
    const onLocked = () => refreshPro();
    window.addEventListener("rb-pro-changed", onLocked);
    return () => { clearInterval(id); window.removeEventListener("rb-pro-changed", onLocked); };
  }, [user, refreshPro]);

  const toggleEvening = () => {
    setEvening((v) => {
      const next = !v;
      localStorage.setItem("rb_evening", next ? "1" : "0");
      return next;
    });
  };

  const unlockCarer = () => {
    sessionStorage.setItem("rb_carer_unlocked", "1");
    setCarerUnlocked(true);
  };

  const lockCarer = () => {
    sessionStorage.removeItem("rb_carer_unlocked");
    setCarerUnlocked(false);
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    lockCarer();
    setUser(data);
  };

  const register = async (name, email, password, accountType = "individual", orgName = "") => {
    const referralCode = localStorage.getItem("rb_ref");
    const { data } = await api.post("/auth/register", { name, email, password, account_type: accountType, org_name: orgName || undefined, referral_code: referralCode || undefined });
    if (referralCode) localStorage.removeItem("rb_ref");
    lockCarer();
    setUser(data);
  };

  const joinOrg = async (code, name, email, password) => {
    const { data } = await api.post("/org/join", { code, name, email, password });
    lockCarer();
    setUser(data);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    try { const keys = await caches.keys(); await Promise.all(keys.map((k) => caches.delete(k))); } catch { /* ignore */ }
    lockCarer();
    setUser(false);
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser, isPremium: !!user && (user.tier === "premium" || user.tier === "business"), pro, refreshPro, canUsePro: !!user && (user.tier === "premium" || user.tier === "business" || (pro ? pro.state !== "locked" : true)), carerUnlocked, unlockCarer, lockCarer, evening, toggleEvening }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
